import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InventoryIcon from '@mui/icons-material/Inventory';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { useB2BAccess } from '../../hooks/useModuleAccess';
import { MobileFormField } from '../../components/mobile/MobileFormField';
import { BarcodeScannerModal } from '../pos/components/BarcodeScannerModal';
import { BulkVariantCreationDialog } from './components/BulkVariantCreationDialog';
import { generateSkuFromName } from '../../utils/sku';
import { useSkuValidation } from '../../hooks/useSkuValidation';

import type { ProductType, Product } from '../../types';

interface ProductFormData {
  name: string;
  description: string;
  category: string;
  price: number;
  sku: string;
  barcode?: string;
  isVisible: boolean;
  isB2bVisible: boolean;
  productType: ProductType;
}

export const ProductCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { hasAccess: hasB2BAccess } = useB2BAccess();
  const [scannerModalOpen, setScannerModalOpen] = useState(false);
  const [createdProduct, setCreatedProduct] = useState<Product | null>(null);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);

  const {
    handleSubmit,
    watch,
    setValue,
    control,
  } = useForm<ProductFormData>({
    defaultValues: {
      isVisible: true,
      isB2bVisible: false,
      productType: 'sellable',
    },
  });

  const isVisible = watch('isVisible');
  const isB2bVisible = watch('isB2bVisible');
  const productType = watch('productType');
  const productName = watch('name');
  const currentSku = watch('sku');

  // Real-time SKU availability validation
  const { isAvailable, isChecking } = useSkuValidation({
    sku: currentSku,
    enabled: !!currentSku, // Only check when SKU has a value
  });

  // Auto-generate SKU from product name
  useEffect(() => {
    // Only auto-generate if SKU is empty and name is not empty
    if (!currentSku && productName && productName.trim().length > 0) {
      const generatedSku = generateSkuFromName(productName);
      setValue('sku', generatedSku);
    }
  }, [productName, currentSku, setValue]);

  const createMutation = useMutation({
    mutationFn: (data: any) => axiosInstance.post('/products', data),
    onSuccess: (response) => {
      const product = response.data;
      setCreatedProduct(product);
      showMessage('Product created successfully. You can now add variants.', 'success');
      // Show variant creation dialog
      setVariantDialogOpen(true);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create product', 'error');
    },
  });

  const onSubmit = (data: ProductFormData) => {
    const transformedData = {
      name: data.name,
      description: data.description,
      category: data.category,
      price: Number(data.price),
      sku: data.sku,
      barcode: data.barcode || undefined,
      isVisibleInStorefront: data.productType === 'sellable' ? data.isVisible : false,
      isB2bVisible: data.productType === 'sellable' ? data.isB2bVisible : false,
      productType: data.productType,
    };
    createMutation.mutate(transformedData);
  };

  const handleVariantDialogClose = () => {
    setVariantDialogOpen(false);
    if (createdProduct) {
      // Navigate to edit page after variant dialog closes
      navigate(`/app/products/edit/${createdProduct.id}`);
    }
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Create Product
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <MobileFormField
              name="name"
              control={control}
              rules={{ required: 'Product name is required' }}
              label="Product Name"
              placeholder="Enter product name"
              margin="normal"
              fullWidth
              autoFocus
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  placeholder="Enter product description"
                />
              )}
            />

            <Controller
              name="category"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  select
                  label="Category"
                >
                  <MenuItem value="Electronics">Electronics</MenuItem>
                  <MenuItem value="Clothing">Clothing</MenuItem>
                  <MenuItem value="Food">Food</MenuItem>
                  <MenuItem value="Books">Books</MenuItem>
                  <MenuItem value="Home & Garden">Home & Garden</MenuItem>
                  <MenuItem value="Sports">Sports</MenuItem>
                  <MenuItem value="Toys">Toys</MenuItem>
                  <MenuItem value="Beauty">Beauty</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />

            <MobileFormField
              name="price"
              control={control}
              rules={{
                required: 'Price is required',
                valueAsNumber: true,
                min: { value: 0, message: 'Price must be positive' },
              }}
              label="Price (DZD)"
              type="number"
              placeholder="0.00"
              margin="normal"
              fullWidth
              inputProps={{ step: '0.01' }}
            />

            <MobileFormField
              name="sku"
              control={control}
              rules={{
                required: 'SKU is required',
                maxLength: { value: 100, message: 'SKU must not exceed 100 characters' },
              }}
              label="SKU (Stock Keeping Unit)"
              helperText={
                isChecking
                  ? "⏳ Checking availability..."
                  : isAvailable === false
                  ? "⚠️ SKU already exists in your organization"
                  : isAvailable === true
                  ? "✓ SKU is available"
                  : "Auto-generated from name, but you can edit it"
              }
              placeholder="e.g. PROD-001"
              margin="normal"
              fullWidth
              error={isAvailable === false}
            />

            <Controller
              name="barcode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  label="Code-barres (optionnel)"
                  helperText="EAN-8, EAN-13, UPC ou autre format"
                  placeholder="e.g. 1234567890123"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Scanner avec la caméra">
                          <IconButton
                            onClick={() => setScannerModalOpen(true)}
                            edge="end"
                            color="primary"
                          >
                            <CameraAltIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* Product Type Selector */}
            <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }}>
              <FormLabel component="legend">Type de produit</FormLabel>
              <Controller
                name="productType"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="sellable"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StorefrontIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          Vendable (visible au POS)
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="internal"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <InventoryIcon sx={{ fontSize: 18, color: 'info.main' }} />
                          Interne (usage interne uniquement)
                        </Box>
                      }
                    />
                  </RadioGroup>
                )}
              />
              {productType === 'internal' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Les produits internes (nettoyage, sacs, fournitures) n'apparaissent pas au POS ni sur le storefront.
                </Typography>
              )}
            </FormControl>

            {productType === 'sellable' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isVisible}
                    onChange={(e) => setValue('isVisible', e.target.checked)}
                  />
                }
                label="Visible on Storefront (Published)"
                sx={{ mt: 2, display: 'block' }}
              />
            )}

            {hasB2BAccess && productType === 'sellable' && (
              <Tooltip title="When enabled, this product will be visible to approved B2B resellers in your B2B portal">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isB2bVisible}
                      onChange={(e) => setValue('isB2bVisible', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Visible to B2B Resellers
                    </Box>
                  }
                  sx={{ mt: 1, display: 'block' }}
                />
              </Tooltip>
            )}

            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/app/products')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        open={scannerModalOpen}
        onClose={() => setScannerModalOpen(false)}
        onScan={(barcode) => {
          setValue('barcode', barcode);
          showMessage(`Code-barres scanné: ${barcode}`, 'success');
        }}
      />

      {/* Variant Creation Dialog */}
      {createdProduct && (
        <BulkVariantCreationDialog
          open={variantDialogOpen}
          onClose={handleVariantDialogClose}
          product={createdProduct}
        />
      )}
    </Box>
  );
};
